import { Formik } from 'formik';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import * as Yup from 'yup';

import { tokens } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { CHECKOUT_DELIVERY_FORM } from '@fhs-legacy/frontend/src/utils/test-ids';

import DeliveryInstructionsField from './delivery-instructions-field';
import PhoneNumberField, { sanitizePhoneNumber } from './phone-number-field';
import PhoneNumberRequiredCalloutToggle from './phone-number-required-callout-toggle';
import SaveAddressCheckbox from './save-address-checkbox';
import UnitNumberField from './unit-number-field';

const PHONE_CHAR_REGEX = /^[\d+\-()\s]+$/; // Allowed: digits, +, -, (, ), space
const PLUS_ONE_REGEX = /^\+1[2-9]\d{9}$/; // +1 followed by area code (2-9) + 9 digits
const PREFIXED_REGEX = /^1[2-9]\d{9}$/; // 1 followed by area code (2-9) + 9 digits
const TEN_DIGIT_REGEX = /^[2-9]\d{9}$/; // Area code (2-9) + 9 digits (NANP format)

const validatePhoneNumber = (value: string | undefined): boolean => {
  if (!value || value.length === 0) {
    return false;
  }

  // Early return for all characters
  if (!PHONE_CHAR_REGEX.test(value)) {
    return false;
  }

  const cleaned = value.replace(/[^\d+]/g, '');

  if (cleaned.startsWith('+1')) {
    return cleaned.length === 12 && PLUS_ONE_REGEX.test(cleaned);
  } else if (cleaned.startsWith('1')) {
    return cleaned.length === 11 && PREFIXED_REGEX.test(cleaned);
  } else {
    return cleaned.length === 10 && TEN_DIGIT_REGEX.test(cleaned);
  }
};

const validationSchema = Yup.object({
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .test('phone', 'Please enter a valid US/Canada phone number', validatePhoneNumber),
  unitNumber: Yup.string().optional(),
  deliveryInstructions: Yup.string().max(512, 'Instructions must be 512 characters or less'),
});

interface DeliveryFormData {
  phoneNumber: string;
  unitNumber: string;
  deliveryInstructions: string;
}

export default function DeliveryForm() {
  const { orderPhoneNumber, deliveryAddress, deliveryInstructions } = useOrderContext();

  const initialValues: DeliveryFormData = useMemo(
    () => ({
      phoneNumber: sanitizePhoneNumber(orderPhoneNumber || ''),
      unitNumber: deliveryAddress?.addressLine2 || '',
      deliveryInstructions: deliveryInstructions || '',
    }),
    [orderPhoneNumber, deliveryAddress?.addressLine2, deliveryInstructions]
  );

  return (
    <View testID={CHECKOUT_DELIVERY_FORM} style={styles.container}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={() => {}}
        enableReinitialize
        validateOnMount
        validateOnChange={true}
        validateOnBlur={true}
      >
        {() => {
          return (
            <View style={styles.formContent}>
              <UnitNumberField />
              <DeliveryInstructionsField />
              <PhoneNumberField />
              <PhoneNumberRequiredCalloutToggle />
              <SaveAddressCheckbox />
            </View>
          );
        }}
      </Formik>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 13,
    paddingHorizontal: 16,
    borderBottomColor: tokens.colors.$black10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  formContent: {
    gap: 4,
  },
});
